import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { HiOutlineChatAlt2, HiOutlineOfficeBuilding, HiUser, HiDocumentText, HiRefresh } from 'react-icons/hi';

interface AIHistoryListProps {
    history: Array<{
        _id: string;
        type: string;
        message: string;
        aiResponse?: {
            companyName?: string;
            jobTitle?: string;
            resume?: string;
            referralContact?: string;
            link?: string;
        };
        createdAt: string;
    }>;
    onReuse: (message: string, type: string, historyItem: any) => void;
}

const HistoryBadge: React.FC<{ icon: React.ReactNode; label: string; value?: string }> = ({ icon, label, value }) => {
    if (!value) return null;
    
    return (
        <div className="flex items-center text-sm text-gray-600 bg-gray-50 rounded-full px-3 py-1">
            <span className="text-gray-400 mr-1.5">{icon}</span>
            <span className="font-medium">{label}:</span>
            <span className="ml-1 truncate max-w-[150px]">{value}</span>
        </div>
    );
};

export const AIHistoryList: React.FC<AIHistoryListProps> = ({ history, onReuse }) => {
    if (history.length === 0) {
        return (
            <div className="mt-8 p-6 text-center bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500">No interaction history yet.</p>
                <p className="text-sm text-gray-400 mt-1">Your AI interactions will appear here</p>
            </div>
        );
    }

    return (
        <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Interaction History</h3>
                <span className="text-sm text-gray-500">{history.length} items</span>
            </div>
            
            <div className="space-y-4">
                {history.map((item) => (
                    <div key={item._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                                            {item.type}
                                        </span>
                                        <span className="ml-2 text-xs text-gray-500">
                                            {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                                        </span>
                                    </div>
                                    
                                    <p className="mt-2 text-gray-800 line-clamp-2">
                                        <HiOutlineChatAlt2 className="inline mr-1.5 text-gray-400" />
                                        {item.message}
                                    </p>

                                    {item.aiResponse && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <HistoryBadge 
                                                icon={<HiOutlineOfficeBuilding />} 
                                                label="Company" 
                                                value={item.aiResponse.companyName} 
                                            />
                                            <HistoryBadge 
                                                icon={<HiDocumentText />} 
                                                label="Resume" 
                                                value={item.aiResponse.resume} 
                                            />
                                            <HistoryBadge 
                                                icon={<HiUser />} 
                                                label="Contact" 
                                                value={item.aiResponse.referralContact} 
                                            />
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => onReuse(item.message, item.type, item)}
                                    className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                >
                                    <HiRefresh className="mr-1.5 h-4 w-4" />
                                    Reuse
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AIHistoryList;